// useApi.js

import { useEffect, useMemo, useState } from 'react';

import {
  getBaseApiLimits,
  getBaseOpenApiSpec,
  getBaseSchemaDoc,
} from 'services/api/ApiAccessService';

import {
  cleanString,
  extractTypeRowsFromSchemaDoc,
  isIntegratorAllowedMethod,
  normalizeTypeCatalogRows,
  resolveOpenApiDocument,
  resolveOpenApiGeneratedAt,
} from '../apiUtils';

export default function useApi({
  baseId,
  getAllNodeTypes,
  isNodeTypesReady,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openapi, setOpenapi] = useState(null);
  const [schemaDoc, setSchemaDoc] = useState(null);
  const [apiLimits, setApiLimits] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadDocs = async () => {
      if (!baseId) {
        setApiLimits(null);
        setOpenapi(null);
        setSchemaDoc(null);
        setError('Select an active base to view Base API docs.');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const [openapiResult, schemaResult, limitsResult] = await Promise.allSettled([
          getBaseOpenApiSpec({ baseId }),
          getBaseSchemaDoc({ baseId }),
          getBaseApiLimits({ baseId }),
        ]);

        if (cancelled) return;

        const openapiFailed = openapiResult.status === 'rejected';
        const schemaFailed = schemaResult.status === 'rejected';

        if (openapiFailed || schemaFailed) {
          throw openapiFailed
            ? openapiResult.reason
            : schemaResult.reason;
        }

        setOpenapi(openapiResult.value || null);
        setSchemaDoc(schemaResult.value || null);
        setApiLimits(limitsResult.status === 'fulfilled' ? (limitsResult.value || null) : null);
      } catch (loadError) {
        if (cancelled) return;

        setApiLimits(null);
        setOpenapi(null);
        setSchemaDoc(null);
        setError(loadError?.message || 'Failed to load Base API docs.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDocs();

    return () => {
      cancelled = true;
    };
  }, [baseId]);

  const openapiDoc = useMemo(() => resolveOpenApiDocument(openapi), [openapi]);

  const openapiGeneratedAt = useMemo(() => {
    return resolveOpenApiGeneratedAt(openapi, openapiDoc);
  }, [openapi, openapiDoc]);

  const openapiPathRows = useMemo(() => {
    const paths = openapiDoc?.paths;
    if (!paths || typeof paths !== 'object') return [];

    return Object.entries(paths)
      .map(([pathKey, value]) => {
        const methods = value && typeof value === 'object'
          ? Object.entries(value)
            .filter(([, methodValue]) => methodValue && typeof methodValue === 'object')
            .map(([methodKey, methodValue]) => ({
              method: cleanString(methodKey).toUpperCase(),
              summary: cleanString(methodValue?.summary) || null,
            }))
          : [];

        return {
          path: pathKey,
          methods,
        };
      })
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [openapiDoc]);

  const visibleOpenapiPathRows = useMemo(() => {
    return openapiPathRows
      .map((row) => {
        const filteredMethods = (row.methods || []).filter((methodRow) => {
          return isIntegratorAllowedMethod(row.path, methodRow.method);
        });

        return {
          ...row,
          methods: filteredMethods,
        };
      })
      .filter((row) => row.methods.length > 0);
  }, [openapiPathRows]);

  const nodeTypeCacheReady = typeof isNodeTypesReady === 'function' ? isNodeTypesReady() : false;

  const cachedTypeRows = useMemo(() => {
    if (!nodeTypeCacheReady) {
      return [];
    }

    return normalizeTypeCatalogRows(
      typeof getAllNodeTypes === 'function' ? getAllNodeTypes() : [],
    );
  }, [getAllNodeTypes, nodeTypeCacheReady]);

  const schemaTypeRows = useMemo(() => extractTypeRowsFromSchemaDoc(schemaDoc), [schemaDoc]);

  const schemaTypes = useMemo(() => {
    const mergedByTypeId = new Map();

    schemaTypeRows.forEach((row) => {
      const typeId = cleanString(row?.typeId || row?.id || row?.key);
      if (!typeId) return;
      mergedByTypeId.set(typeId.toLowerCase(), {
        ...row,
        typeId,
      });
    });

    cachedTypeRows.forEach((row) => {
      const typeId = cleanString(row?.typeId || row?.id || row?.key);
      if (!typeId) return;

      const key = typeId.toLowerCase();
      const existing = mergedByTypeId.get(key);
      if (!existing) {
        mergedByTypeId.set(key, {
          ...row,
          typeId,
        });
        return;
      }

      const existingFields = Array.isArray(existing?.fields) ? existing.fields : [];
      const candidateFields = Array.isArray(row?.fields) ? row.fields : [];

      mergedByTypeId.set(key, {
        ...existing,
        ...row,
        typeId,
        label: cleanString(row?.label) || cleanString(existing?.label) || '',
        fields: candidateFields.length > 0 ? candidateFields : existingFields,
      });
    });

    return Array.from(mergedByTypeId.values()).sort((a, b) => {
      const aLabel = cleanString(a?.label);
      const bLabel = cleanString(b?.label);
      return aLabel.localeCompare(bLabel);
    });
  }, [cachedTypeRows, schemaTypeRows]);

  return {
    loading,
    error,
    apiLimits,
    schemaDoc,
    openapiGeneratedAt,
    visibleOpenapiPathRows,
    schemaTypes,
    nodeTypeCacheReady,
  };
}
